/**
 * Usage actions and BeHeard's LOCAL NONCOMMERCIAL SAFETY LIMITS.
 *
 * AUTHORITY: LOCAL_NONCOMMERCIAL_SAFETY_LIMIT (docs/PLAN-AUTHORITY.md §"Local
 * safety allowances"). These numbers cap how much provider-cost work (STT,
 * TTS, retrieval, uploads, full listens, recruiter actions) one principal can
 * trigger per calendar month / day. They are product-safety capacities, NOT
 * credits: they carry no price, cannot be bought, discounted, granted or
 * settled, and are never shown to users as purchasable. Commercial plans,
 * credit pricing, balances and settlement are Central's.
 *
 * Why they are not per-action capacities: each pool ('practice', 'coaching',
 * 'listen') is SHARED by several actions with different unit weights (e.g. a
 * content upload uses 5 practice units, a voice analysis 1), so no set of
 * independent per-action caps admits exactly the same requests. Enforcement is
 * therefore kept byte-for-byte as it was and only relabelled; the Central
 * cutover (registering these actions as Central metered actions) is recorded in
 * docs/PLAN-AUTHORITY.md as an external dependency.
 *
 * The usage_records table keeps its historical column names (credit_type,
 * credits_consumed): renaming them needs a migration, and they hold the pool
 * and the units, nothing monetary.
 */

/** The authority label for every number in this file. */
export const LOCAL_NONCOMMERCIAL_SAFETY_LIMIT = 'LOCAL_NONCOMMERCIAL_SAFETY_LIMIT' as const;
export type UsageAllowanceAuthority = typeof LOCAL_NONCOMMERCIAL_SAFETY_LIMIT;

// ============================================
// Usage Actions
// ============================================

export type UsageAction =
  | 'voice_practice_classic' // Single practice session with STT
  | 'voice_practice_live_minute' // Per minute of live streaming practice
  | 'tts_coach_response' // AI voice synthesis for coach feedback
  | 'listen_full_pitch' // Listening to full portfolio audio
  | 'listen_snippet' // Listening to preview (usually free)
  | 'exam_attempt' // Starting a domain certification exam
  | 'portfolio_profile_view' // Viewing a public portfolio (usually free)
  | 'evaluate_candidate' // Recruiter evaluating a candidate
  | 'contact_candidate' // Recruiter sending contact request
  | 'content_upload' // Uploading content for RAG indexing (file or link)
  | 'content_query' // RAG retrieval query for practice session
  | 'coaching_session_one_time' // One-time human coaching session booking
  | 'interview_prep_extraction'; // Interview prep: the LLM extraction of a job description

/** A shared monthly safety pool. Not a credit type: nothing is priced or bought. */
export type AllowancePool = 'practice' | 'coaching' | 'listen';

export interface UsageAllowanceRule {
  action: UsageAction;
  /** Safety units one use takes from its pool (or per minute for live). 0 = not metered. */
  allowanceUnits: number;
  /** The shared monthly safety pool the units come from. */
  allowancePool: AllowancePool;
  /** For free tier: max uses per day (undefined = unlimited) */
  freeTierLimitPerDay?: number;
  /** For free tier: max uses per month (undefined = unlimited) */
  freeTierLimitPerMonth?: number;
  /**
   * EVERY tier: max uses per calendar day (undefined = none). A noncommercial
   * safety cap, not a plan limit: the same number on every plan. Each admitted
   * use is recorded (with 0 units when `allowanceUnits` is 0) so it counts, and
   * a released use (provider failure) is given back.
   */
  safetyLimitPerDay?: number;
  /** Description for display */
  description: string;
}

// ============================================
// Plan Capabilities
// ============================================

export interface PlanCapabilities {
  /** Local plan key (legacy compatibility; commercial plans are Central's) */
  planKey: string; // 'free' | 'pro' | 'pro_ai'
  /** Display label */
  label: string;
  /** Description */
  description: string;

  // Feature flags
  canUseAI: boolean;
  canUseVoicePractice: boolean;
  canUseLiveVoice: boolean;
  canListenFullPitches: boolean;
  canTakeExams: boolean;

  /**
   * Safety units per calendar month in each pool (LOCAL_NONCOMMERCIAL_SAFETY_LIMIT).
   * Not credits: no price, no purchase, no rollover.
   */
  monthlyAllowance: Record<AllowancePool, number>;

  // Unlimited flags (the pool is not metered for this tier)
  unlimitedPractice?: boolean;
  unlimitedListening?: boolean;
}

// ============================================
// Usage Check Result
// ============================================

export type UsageBlockReason =
  | 'monthly_allowance_reached'
  | 'plan_not_allowed'
  | 'daily_limit_reached'
  | 'monthly_limit_reached'
  | 'plan_limit';

export interface UsageCheckResult {
  allowed: boolean;
  reason?: UsageBlockReason;
  /** Remaining daily uses if applicable */
  remainingDaily?: number;
  /** Remaining monthly uses if applicable */
  remainingMonthly?: number;
  /** Safety units this use takes (0 when the pool is unlimited for the tier) */
  allowanceUnits?: number;
  /** Units left in the action's pool this calendar month, before this use */
  remainingAllowance?: number;
  /** Upgrade prompt for blocked actions */
  upgradePrompt?: string;
  /**
   * True when the refusal is an every-plan noncommercial SAFETY cap
   * (safetyLimitPerDay), not a plan limit: answered 429, no upgrade offered.
   */
  safetyLimitReached?: boolean;
}

// ============================================
// Per-action safety units (LOCAL_NONCOMMERCIAL_SAFETY_LIMIT)
// ============================================

export const USAGE_ALLOWANCE_RULES: UsageAllowanceRule[] = [
  {
    action: 'voice_practice_classic',
    allowanceUnits: 1,
    allowancePool: 'practice',
    freeTierLimitPerDay: 2,
    freeTierLimitPerMonth: 10,
    // Every-plan noncommercial safety cap — the SAME number as
    // config/conversationSafety.ts VOICE_ANALYSIS_SAFETY_MAX_DAILY (this file
    // imports nothing; a unit test pins the two equal). Applies even where the
    // practice pool is not metered per use (unlimitedPractice).
    safetyLimitPerDay: 200,
    description: 'Classic voice practice session with AI feedback',
  },
  {
    action: 'voice_practice_live_minute',
    allowanceUnits: 2,
    allowancePool: 'practice',
    freeTierLimitPerDay: 0, // Not available on free
    freeTierLimitPerMonth: 0,
    description: 'Live streaming voice practice per minute',
  },
  {
    action: 'tts_coach_response',
    allowanceUnits: 1,
    allowancePool: 'coaching',
    freeTierLimitPerDay: 0, // Not available on free
    freeTierLimitPerMonth: 0,
    description: 'AI voice synthesis for coach feedback',
  },
  {
    action: 'listen_full_pitch',
    allowanceUnits: 1,
    allowancePool: 'listen',
    freeTierLimitPerDay: 3,
    freeTierLimitPerMonth: 20,
    description: 'Listen to a full portfolio pitch',
  },
  {
    action: 'listen_snippet',
    allowanceUnits: 0, // Free
    allowancePool: 'listen',
    description: 'Listen to a pitch preview',
  },
  {
    action: 'exam_attempt',
    allowanceUnits: 0, // Free - part of plan
    allowancePool: 'practice',
    freeTierLimitPerDay: 1,
    freeTierLimitPerMonth: 3,
    description: 'Attempt a domain certification exam',
  },
  {
    action: 'portfolio_profile_view',
    allowanceUnits: 0, // Free
    allowancePool: 'listen',
    description: 'View a public portfolio profile',
  },
  {
    action: 'evaluate_candidate',
    allowanceUnits: 1,
    allowancePool: 'listen',
    freeTierLimitPerDay: 5,
    freeTierLimitPerMonth: 30,
    description: 'Evaluate a candidate in a recruiter session',
  },
  {
    action: 'contact_candidate',
    allowanceUnits: 2,
    allowancePool: 'listen',
    freeTierLimitPerDay: 3,
    freeTierLimitPerMonth: 15,
    description: 'Send a contact request to a candidate',
  },
  {
    action: 'content_upload',
    allowanceUnits: 5,
    allowancePool: 'practice',
    freeTierLimitPerDay: 2,
    freeTierLimitPerMonth: 5,
    description: 'Upload content for AI-assisted practice (file or link)',
  },
  {
    action: 'content_query',
    allowanceUnits: 1,
    allowancePool: 'practice',
    freeTierLimitPerDay: 5,
    freeTierLimitPerMonth: 20,
    description: 'AI retrieval of relevant content during practice',
  },
  {
    action: 'coaching_session_one_time',
    allowanceUnits: 0, // Not metered here (booking is a coaching flow, not a safety-limited provider action)
    allowancePool: 'coaching',
    description: 'Book a one-time session with a human coach',
  },
  {
    // Preparing an interview practice runs an extraction LLM call over the
    // job description. It was plan-checked (a practice start) but never
    // metered, so repeated prepares bought unbounded LLM calls. No commercial
    // number: the conversation safety budget — the SAME number as
    // config/conversationSafety.ts CONVERSATION_SAFETY_MAX_DAILY_STARTS (this
    // file is compiled into the web bundle and imports nothing; a unit test
    // pins the two equal) — per calendar day, every plan; 0 units, so no pool
    // is touched.
    action: 'interview_prep_extraction',
    allowanceUnits: 0,
    allowancePool: 'practice',
    safetyLimitPerDay: 50,
    description: 'Prepare an interview practice from a job description (AI extraction)',
  },
];

// ============================================
// Plan Capability Definitions
// ============================================

export const PLAN_CAPABILITIES: PlanCapabilities[] = [
  {
    planKey: 'free',
    label: 'Free',
    description: 'Get started with limited practice and listening',
    canUseAI: false,
    canUseVoicePractice: true, // Limited
    canUseLiveVoice: false,
    canListenFullPitches: true, // Limited
    canTakeExams: true, // Limited
    monthlyAllowance: {
      practice: 5,
      coaching: 0,
      listen: 20,
    },
  },
  {
    planKey: 'pro',
    label: 'Pro',
    description: 'More practice, analytics, and marketplace access',
    canUseAI: false, // No AI voice
    canUseVoicePractice: true,
    canUseLiveVoice: false,
    canListenFullPitches: true,
    canTakeExams: true,
    monthlyAllowance: {
      practice: 50,
      coaching: 0,
      listen: 100,
    },
  },
  {
    planKey: 'pro_ai',
    label: 'Pro + AI Voice',
    description: 'Full AI voice coaching and premium features',
    canUseAI: true,
    canUseVoicePractice: true,
    canUseLiveVoice: true,
    canListenFullPitches: true,
    canTakeExams: true,
    monthlyAllowance: {
      practice: 999, // Effectively unlimited
      coaching: 50,
      listen: 999,
    },
    unlimitedPractice: true,
    unlimitedListening: true,
  },
];

// ============================================
// Helper Functions
// ============================================

/**
 * The safety-unit rule for an action
 */
export function getUsageAllowanceRule(action: UsageAction): UsageAllowanceRule | undefined {
  return USAGE_ALLOWANCE_RULES.find((c) => c.action === action);
}

/**
 * Get plan capabilities by plan key
 */
export function getPlanCapabilities(planKey: string): PlanCapabilities {
  return PLAN_CAPABILITIES.find((p) => p.planKey === planKey) ?? PLAN_CAPABILITIES[0];
}

/**
 * Check if an action is allowed on a plan (ignoring the monthly safety pools)
 */
export function isActionAllowedOnPlan(
  action: UsageAction,
  capabilities: PlanCapabilities
): boolean {
  switch (action) {
    case 'voice_practice_live_minute':
      return capabilities.canUseLiveVoice;
    case 'tts_coach_response':
      return capabilities.canUseAI;
    case 'voice_practice_classic':
      return capabilities.canUseVoicePractice;
    case 'listen_full_pitch':
      return capabilities.canListenFullPitches;
    case 'exam_attempt':
      return capabilities.canTakeExams;
    default:
      return true;
  }
}
